import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  DatePicker,
  Statistic,
  Tag,
  Empty,
  Spin,
  Modal,
  Image,
  Space,
  Divider,
  message,
  Descriptions,
  Alert,
  Tabs,
  Table,
} from "antd";

import {
  ArrowLeftOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  WalletOutlined,
  BankOutlined,
  UserOutlined,
  DollarOutlined,
  PictureOutlined,
  CarOutlined,
  GlobalOutlined,
  EyeOutlined,
  PlusOutlined,
  FileTextOutlined,
  SwapOutlined,
  ShopOutlined,
  PhoneOutlined,
  DownloadOutlined,
  HomeOutlined,
  WarningOutlined,
  CompassOutlined,
  AimOutlined,
  IdcardOutlined,
  AppstoreOutlined,
  CreditCardOutlined,
  TableOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getRequest } from "../../../Utils/AppApi";

import "./staffVisitDetails.css";

const { RangePicker } = DatePicker;

const MAX_RANGE_DAYS = 31; // inclusive day count

/* ============================================================
   HELPERS
============================================================ */
const buildMapsUrl = (lat, lng, zoom = 17) => {
  if (!lat || !lng) return null;
  return `https://www.google.com/maps?q=${lat},${lng}&z=${zoom}&hl=en`;
};

const openMaps = (lat, lng, zoom = 17) => {
  const url = buildMapsUrl(lat, lng, zoom);
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

const toRad = (v) => (v * Math.PI) / 180;

const distanceKm = (lat1, lon1, lat2, lon2) => {
  if (
    lat1 == null ||
    lon1 == null ||
    lat2 == null ||
    lon2 == null ||
    isNaN(lat1) ||
    isNaN(lon1) ||
    isNaN(lat2) ||
    isNaN(lon2)
  )
    return null;

  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const fmtDistance = (km) => {
  if (km == null || km === 0) return "0 m";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(2)} km`;
};

const fmtMoney = (n) => {
  if (n == null || n === "") return "-";
  const num = Number(n);
  if (isNaN(num)) return "-";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const showVal = (v) => {
  if (v === null || v === undefined || v === "") return "-";
  return v;
};

const columnHasData = (visits, getter) =>
  visits.some((v) => {
    const val = getter(v);
    return val !== null && val !== undefined && val !== "" && val !== 0;
  });

const hasBalance = (v) =>
  v !== null &&
  v !== undefined &&
  v !== "" &&
  !(typeof v === "number" && v === 0) &&
  !(typeof v === "string" && Number(v) === 0);

/* ============================================================
   BALANCE LABELS
============================================================ */
const LABEL_WALLET = "Wallet Balance";
const LABEL_SRPAY = "SR Pay Outstanding";
const LABEL_SRKHATA = "SR Khata Outstanding";

/* ============================================================
   COMPONENT
============================================================ */
function StaffVisitDetails() {
  const history = useHistory();
  const location = useLocation();

  const staffName = location.state?.staffName || "";
  const staffUsername = location.state?.staffUsername || "";
  const staffId = location.pathname.split("/")[3];

  const [loading, setLoading] = useState(false);
  const [visits, setVisits] = useState([]);
  const [summary, setSummary] = useState(null);

  // Date range
  const today = useMemo(() => moment(), []);
  const [dateRange, setDateRange] = useState([today, today]);

  const [activeTab, setActiveTab] = useState("timeline");

  const [selectedVisit, setSelectedVisit] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const startDateStr = dateRange?.[0]?.format("YYYY-MM-DD") || null;
  const endDateStr = dateRange?.[1]?.format("YYYY-MM-DD") || null;

  useEffect(() => {
    if (startDateStr && endDateStr) {
      fetchVisits();
    }
    // eslint-disable-next-line
  }, [startDateStr, endDateStr]);

  /* ---------------------------------------------------------
     BACK BUTTON
  --------------------------------------------------------- */
  const handleBack = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push("/admin/staff");
    }
  };

  /* ---------------------------------------------------------
     Date range change with 31-day cap
  --------------------------------------------------------- */
  const handleDateRangeChange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      setDateRange([moment(), moment()]);
      return;
    }

    const [start, end] = dates;
    const diffDays = end.diff(start, "days") + 1;

    if (diffDays > MAX_RANGE_DAYS) {
      message.warning(
        `Maximum date range allowed is ${MAX_RANGE_DAYS} days`
      );
      return;
    }

    setDateRange([start, end]);
  };

  /* ---------------------------------------------------------
     FETCH
  --------------------------------------------------------- */
  const fetchVisits = async () => {
    setLoading(true);
    try {
      const response = await getRequest(
        `/admin/staff/${staffId}/visits?startDate=${startDateStr}&endDate=${endDateStr}`,
        history
      );

      if (response.success) {
        let visitsData = [];
        if (response?.data?.visits) {
          visitsData = Array.isArray(response.data.visits)
            ? response.data.visits
            : Object.values(response.data.visits).flat();
        }
        visitsData.sort(
          (a, b) => new Date(a.visitDate) - new Date(b.visitDate)
        );
        setVisits(enrichVisits(visitsData));
        setSummary(response?.data?.summary || null);
      } else {
        message.error(response.message);
      }
    } catch (err) {
      console.error(err);
      message.error("Unable to load visit data");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------
     ENRICH WITH DISTANCES
  --------------------------------------------------------- */
  const enrichVisits = (list) => {
    if (!list?.length) return [];

    return list.map((visit, i) => {
      const enriched = { ...visit };

      if (i < list.length - 1) {
        const next = list[i + 1];
        const d = distanceKm(
          visit.latitude,
          visit.longitude,
          next.latitude,
          next.longitude
        );
        if (d != null) {
          const mins = Math.round((d / 40) * 60);
          enriched.distanceToNext = {
            distance: d,
            distanceText: fmtDistance(d),
            duration: mins > 0 ? `${mins} mins` : "< 1 min",
            durationMinutes: mins,
          };
        }
      }

      const dShop = distanceKm(
        visit.latitude,
        visit.longitude,
        visit.shopLatitude,
        visit.shopLongitude
      );
      if (dShop != null) {
        enriched.distanceToShop = {
          distance: dShop,
          distanceText: fmtDistance(dShop),
          isFar: dShop > 0.5,
        };
      }

      return enriched;
    });
  };

  /* ---------------------------------------------------------
     LABEL HELPERS
  --------------------------------------------------------- */
  const getVisitColor = (type) => {
    switch (type) {
      case "cash_collection":
        return "#52c41a";
      case "cash_drop":
        return "#fa8c16";
      case "cash_collection_drop":
        return "#52c41a";
      case "transaction_issue":
        return "#ff4d4f";
      case "new_outlet":
      case "shop_visit":
        return "#722ed1";
      case "re_visit":
      case "re_visit_follow_up_visit":
        return "#13c2c2";
      case "sr_telecom":                        // ⬅ NEW
        return "#eb2f96";
      case "other":
      case "others":
        return "#13c2c2";
      default:
        return "#1677ff";
    }
  };

  const getVisitLabel = (type) => {
    switch (type) {
      case "cash_collection":
        return "Cash Collection";
      case "cash_drop":
        return "Cash Drop";
      case "cash_collection_drop":
        return "Cash Collection / Drop";
      case "transaction_issue":
        return "Transaction Issue";
      case "new_outlet":
        return "New Outlet";
      case "shop_visit":
        return "New Outlet";
      case "re_visit":
        return "Re-Visit";
      case "re_visit_follow_up_visit":
        return "Re-Visit / Follow Up Visit";
      case "sr_telecom":                        // ⬅ NEW
        return "SR Telecom Visit";
      case "other":
        return "Other";
      case "others":
        return "Others";
      default:
        return "Regular Visit";
    }
  };

  const getVisitIcon = (type) => {
    switch (type) {
      case "cash_collection":
        return <WalletOutlined />;
      case "cash_drop":
        return <BankOutlined />;
      case "cash_collection_drop":
        return <WalletOutlined />;
      case "transaction_issue":
        return <ClockCircleOutlined />;
      case "new_outlet":
      case "shop_visit":
        return <PlusOutlined />;
      case "re_visit":
      case "re_visit_follow_up_visit":
        return <SwapOutlined />;
      case "sr_telecom":                        // ⬅ NEW
        return <PhoneOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  const getDisplayName = (visit) => {
    if (visit.visitType === "sr_telecom") {
      return visit.outletName || visit.shopName || "SR Telecom Visit";
    }
    if (visit.shopName) return visit.shopName;
    if (visit.outletName) return visit.outletName;
    if (visit.visitType === "new_outlet" || visit.visitType === "shop_visit")
      return "New Outlet";
    if (visit.visitType === "other" || visit.visitType === "others")
      return visit.visitTitle || "Other Visit";
    return "Unknown Outlet";
  };

  const getGap = (index) => {
    if (index === 0) return null;
    const prev = visits[index - 1];
    const curr = visits[index];
    return moment(curr.visitDate).diff(moment(prev.visitDate), "minutes");
  };

  /* ---------------------------------------------------------
     BALANCE CHIP
  --------------------------------------------------------- */
  const BalanceChip = ({ label, value, icon }) => {
    if (!hasBalance(value)) return null;
    const num = Number(value);
    const isNeg = num < 0;
    return (
      <span className={`balance-chip ${isNeg ? "negative" : "positive"}`}>
        {icon} {label}: {fmtMoney(num)}
      </span>
    );
  };

  /* ---------------------------------------------------------
     EXCEL EXPORT
  --------------------------------------------------------- */
  const handleExportExcel = () => {
    if (!visits.length) {
      message.warning("No visits to export");
      return;
    }

    const rows = visits.map((v, i) => ({
      "#": i + 1,
      Date: moment(v.visitDate).format("DD-MM-YYYY"),
      Time: moment(v.visitDate).format("hh:mm A"),
      "Visit Type": getVisitLabel(v.visitType),
      "Shop Name": v.shopName || v.outletName || "",
      "Owner Name": v.ownerName || "",
      Mobile: v.mobile || "",
      "Shop Type": v.shopType || "",
      "CMS Name": v.cmsName || "",
      Amount: v.amount != null ? v.amount : "",

      // ── SR Telecom fields (blank for other visit types) ──
      "Topup Date": v.topupDate
        ? moment(v.topupDate).format("DD-MM-YYYY")
        : "",
      "Payment Date": v.paymentDate
        ? moment(v.paymentDate).format("DD-MM-YYYY")
        : "",
      Village: v.village || "",

      "Visit Title": v.visitTitle || "",
      Remark: v.remark || "",
      "Visit Latitude": v.latitude ?? "",
      "Visit Longitude": v.longitude ?? "",
      "Visit Location (Google Maps)":
        buildMapsUrl(v.latitude, v.longitude) || "",
      "Shop Latitude": v.shopLatitude ?? "",
      "Shop Longitude": v.shopLongitude ?? "",
      "Shop Location (Google Maps)":
        v.shopLatitude && v.shopLongitude
          ? buildMapsUrl(v.shopLatitude, v.shopLongitude)
          : "",
      "Shop Address": v.shopAddress || "",
      City: v.shopCity || "",
      State: v.shopState || "",
      Pincode: v.shopPostcode || "",
      "Distance to Shop (km)":
        v.distanceToShop?.distance != null
          ? Number(v.distanceToShop.distance.toFixed(3))
          : "",
      "Distance to Next Visit (km)":
        v.distanceToNext?.distance != null
          ? Number(v.distanceToNext.distance.toFixed(3))
          : "",
      "Travel Time to Next (mins)": v.distanceToNext?.durationMinutes ?? "",
      [LABEL_WALLET]: v.walletBalance ?? "",
      [LABEL_SRPAY]: v.khataBalance ?? "",
      [LABEL_SRKHATA]: v.externalBalance ?? "",
      "Duplicate New Visit": v.duplicateNewVisit ? "YES" : "NO",
      "Location Mismatch": v.locationMismatchFlag ? "YES" : "NO",
      "Images Count": v.images?.length || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 4 },   // #
      { wch: 12 },  // Date
      { wch: 10 },  // Time
      { wch: 20 },  // Visit Type
      { wch: 24 },  // Shop Name
      { wch: 20 },  // Owner Name
      { wch: 14 },  // Mobile
      { wch: 22 },  // Shop Type
      { wch: 22 },  // CMS Name
      { wch: 10 },  // Amount
      { wch: 12 },  // Topup Date
      { wch: 12 },  // Payment Date
      { wch: 20 },  // Village
      { wch: 22 },  // Visit Title
      { wch: 30 },  // Remark
      { wch: 14 },  // Visit Latitude
      { wch: 14 },  // Visit Longitude
      { wch: 50 },  // Visit Location (Google Maps)
      { wch: 14 },  // Shop Latitude
      { wch: 14 },  // Shop Longitude
      { wch: 50 },  // Shop Location (Google Maps)
      { wch: 30 },  // Shop Address
      { wch: 14 },  // City
      { wch: 14 },  // State
      { wch: 10 },  // Pincode
      { wch: 20 },  // Distance to Shop (km)
      { wch: 22 },  // Distance to Next Visit (km)
      { wch: 20 },  // Travel Time to Next (mins)
      { wch: 22 },  // Wallet Balance
      { wch: 22 },  // SR Pay Outstanding
      { wch: 22 },  // SR Khata Outstanding
      { wch: 16 },  // Duplicate New Visit
      { wch: 16 },  // Location Mismatch
      { wch: 12 },  // Images Count
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Visits");

    const fileName =
      startDateStr === endDateStr
        ? `Staff_Visits_${staffUsername || staffId}_${startDateStr}.xlsx`
        : `Staff_Visits_${staffUsername || staffId}_${startDateStr}_to_${endDateStr}.xlsx`;

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), fileName);
    message.success("Excel exported successfully");
  };

  /* ---------------------------------------------------------
     STATS
  --------------------------------------------------------- */
  const stats = useMemo(() => {
    if (!visits.length) {
      return {
        first: null,
        last: null,
        activeHours: "-",
        avgVisit: "-",
        totalDistance: 0,
        totalDuration: 0,
        avgShopDistance: 0,
        farCount: 0,
        totalVisits: 0,
      };
    }

    const first = visits[0];
    const last = visits[visits.length - 1];
    const activeMin = moment(last.visitDate).diff(
      moment(first.visitDate),
      "minutes"
    );

    let totalDistance = 0;
    let totalDuration = 0;
    let totalShopDistance = 0;
    let shopDistanceCount = 0;
    let farCount = 0;

    visits.forEach((v) => {
      if (v.distanceToNext) {
        totalDistance += v.distanceToNext.distance || 0;
        totalDuration += v.distanceToNext.durationMinutes || 0;
      }
      if (v.distanceToShop) {
        totalShopDistance += v.distanceToShop.distance || 0;
        shopDistanceCount += 1;
        if (v.distanceToShop.isFar) farCount += 1;
      }
    });

    return {
      first,
      last,
      activeHours:
        activeMin > 0
          ? `${Math.floor(activeMin / 60)}h ${activeMin % 60}m`
          : "0h 0m",
      avgVisit:
        visits.length > 0
          ? `${Math.floor(activeMin / visits.length)} mins`
          : "0 mins",
      totalDistance,
      totalDuration,
      avgShopDistance:
        shopDistanceCount > 0 ? totalShopDistance / shopDistanceCount : 0,
      farCount,
      totalVisits: visits.length,
    };
  }, [visits]);

  const openVisit = (visit) => {
    setSelectedVisit(visit);
    setModalVisible(true);
  };

  /* ---------------------------------------------------------
     TABLE COLUMNS
  --------------------------------------------------------- */
  const tableColumns = useMemo(() => {
    const cols = [];

    cols.push(
      {
        title: "#",
        key: "index",
        width: 50,
        fixed: "left",
        render: (_, __, i) => i + 1,
      },
      {
        title: "Date",
        dataIndex: "visitDate",
        key: "date",
        width: 100,
        fixed: "left",
        render: (v) => moment(v).format("DD-MM-YYYY"),
      },
      {
        title: "Time",
        dataIndex: "visitDate",
        key: "time",
        width: 90,
        render: (v) => moment(v).format("hh:mm A"),
      },
      {
        title: "Visit Type",
        dataIndex: "visitType",
        key: "visitType",
        width: 160,
        render: (v) => (
          <Tag color={getVisitColor(v)} icon={getVisitIcon(v)}>
            {getVisitLabel(v)}
          </Tag>
        ),
      }
    );

    if (columnHasData(visits, (r) => r.shopName || r.outletName)) {
      cols.push({
        title: "Shop Name",
        key: "shopName",
        width: 180,
        render: (_, r) => showVal(r.shopName || r.outletName),
      });
    }

    if (columnHasData(visits, (r) => r.ownerName)) {
      cols.push({
        title: "Owner Name",
        dataIndex: "ownerName",
        key: "ownerName",
        width: 150,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.mobile)) {
      cols.push({
        title: "Mobile",
        dataIndex: "mobile",
        key: "mobile",
        width: 120,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.shopType)) {
      cols.push({
        title: "Shop Type",
        dataIndex: "shopType",
        key: "shopType",
        width: 180,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.cmsName)) {
      cols.push({
        title: "CMS Name",
        dataIndex: "cmsName",
        key: "cmsName",
        width: 180,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.amount)) {
      cols.push({
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: 100,
        render: (v) =>
          v != null ? (
            <span style={{ color: "#c9262c", fontWeight: 600 }}>
              ₹{Number(v).toLocaleString()}
            </span>
          ) : (
            "-"
          ),
      });
    }

    // ── SR Telecom specific columns ──
    if (columnHasData(visits, (r) => r.topupDate)) {
      cols.push({
        title: "Topup Date",
        dataIndex: "topupDate",
        key: "topupDate",
        width: 120,
        render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
      });
    }

    if (columnHasData(visits, (r) => r.paymentDate)) {
      cols.push({
        title: "Payment Date",
        dataIndex: "paymentDate",
        key: "paymentDate",
        width: 120,
        render: (v) => (v ? moment(v).format("DD-MM-YYYY") : "-"),
      });
    }

    if (columnHasData(visits, (r) => r.village)) {
      cols.push({
        title: "Village",
        dataIndex: "village",
        key: "village",
        width: 140,
        render: (v) => showVal(v),
      });
    }
    // ───────────────────────────────────

    if (columnHasData(visits, (r) => r.visitTitle)) {
      cols.push({
        title: "Visit Title",
        dataIndex: "visitTitle",
        key: "visitTitle",
        width: 180,
        render: (v) => showVal(v),
      });
    }

    cols.push({
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      width: 220,
      render: (v) => <span title={v}>{showVal(v)}</span>,
    });

    if (columnHasData(visits, (r) => r.latitude)) {
      cols.push({
        title: "Visit Lat",
        dataIndex: "latitude",
        key: "latitude",
        width: 110,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.longitude)) {
      cols.push({
        title: "Visit Long",
        dataIndex: "longitude",
        key: "longitude",
        width: 110,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.latitude)) {
      cols.push({
        title: "Visit Map",
        key: "visitMap",
        width: 100,
        render: (_, r) =>
          buildMapsUrl(r.latitude, r.longitude) ? (
            <Button
              size="small"
              type="link"
              icon={<GlobalOutlined />}
              onClick={() => openMaps(r.latitude, r.longitude)}
            >
              Open
            </Button>
          ) : (
            "-"
          ),
      });
    }

    if (columnHasData(visits, (r) => r.shopLatitude)) {
      cols.push({
        title: "Shop Lat",
        dataIndex: "shopLatitude",
        key: "shopLatitude",
        width: 110,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.shopLongitude)) {
      cols.push({
        title: "Shop Long",
        dataIndex: "shopLongitude",
        key: "shopLongitude",
        width: 110,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.shopLatitude)) {
      cols.push({
        title: "Shop Map",
        key: "shopMap",
        width: 100,
        render: (_, r) =>
          buildMapsUrl(r.shopLatitude, r.shopLongitude) ? (
            <Button
              size="small"
              type="link"
              icon={<GlobalOutlined />}
              onClick={() => openMaps(r.shopLatitude, r.shopLongitude)}
            >
              Open
            </Button>
          ) : (
            "-"
          ),
      });
    }

    if (columnHasData(visits, (r) => r.shopAddress)) {
      cols.push({
        title: "Shop Address",
        dataIndex: "shopAddress",
        key: "shopAddress",
        width: 200,
        render: (v) => <span title={v}>{showVal(v)}</span>,
      });
    }

    if (columnHasData(visits, (r) => r.shopCity)) {
      cols.push({
        title: "City",
        dataIndex: "shopCity",
        key: "shopCity",
        width: 120,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.shopState)) {
      cols.push({
        title: "State",
        dataIndex: "shopState",
        key: "shopState",
        width: 120,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.shopPostcode)) {
      cols.push({
        title: "Pincode",
        dataIndex: "shopPostcode",
        key: "shopPostcode",
        width: 100,
        render: (v) => showVal(v),
      });
    }

    if (columnHasData(visits, (r) => r.distanceToShop)) {
      cols.push({
        title: "Dist to Shop",
        key: "distShop",
        width: 130,
        render: (_, r) =>
          r.distanceToShop ? (
            <Tag color={r.distanceToShop.isFar ? "red" : "green"}>
              {r.distanceToShop.distanceText}
            </Tag>
          ) : (
            "-"
          ),
      });
    }

    if (columnHasData(visits, (r) => r.distanceToNext)) {
      cols.push(
        {
          title: "Dist to Next",
          key: "distNext",
          width: 130,
          render: (_, r) =>
            r.distanceToNext ? (
              <Tag color="orange">{r.distanceToNext.distanceText}</Tag>
            ) : (
              "-"
            ),
        },
        {
          title: "Travel to Next",
          key: "travelNext",
          width: 120,
          render: (_, r) =>
            r.distanceToNext ? r.distanceToNext.duration : "-",
        }
      );
    }

    if (columnHasData(visits, (r) => hasBalance(r.walletBalance))) {
      cols.push({
        title: LABEL_WALLET,
        dataIndex: "walletBalance",
        key: "walletBalance",
        width: 150,
        render: (v) =>
          !hasBalance(v) ? (
            "-"
          ) : (
            <span
              style={{
                color: Number(v) < 0 ? "#cf1322" : "#389e0d",
                fontWeight: 600,
              }}
            >
              {fmtMoney(v)}
            </span>
          ),
      });
    }

    if (columnHasData(visits, (r) => hasBalance(r.khataBalance))) {
      cols.push({
        title: LABEL_SRPAY,
        dataIndex: "khataBalance",
        key: "khataBalance",
        width: 160,
        render: (v) =>
          !hasBalance(v) ? (
            "-"
          ) : (
            <span
              style={{
                color: Number(v) > 0 ? "#cf1322" : "#389e0d",
                fontWeight: 600,
              }}
            >
              {fmtMoney(-v)}
            </span>
          ),
      });
    }

    if (columnHasData(visits, (r) => hasBalance(r.externalBalance))) {
      cols.push({
        title: LABEL_SRKHATA,
        dataIndex: "externalBalance",
        key: "externalBalance",
        width: 170,
        render: (v) =>
          !hasBalance(v) ? (
            "-"
          ) : (
            <span
              style={{
                color: Number(v) < 0 ? "#cf1322" : "#389e0d",
                fontWeight: 600,
              }}
            >
              {fmtMoney(v)}
            </span>
          ),
      });
    }

    if (columnHasData(visits, (r) => r.duplicateNewVisit)) {
      cols.push({
        title: "Duplicate",
        dataIndex: "duplicateNewVisit",
        key: "duplicateNewVisit",
        width: 110,
        render: (v) => (v ? <Tag color="red">YES</Tag> : <Tag>NO</Tag>),
      });
    }

    if (columnHasData(visits, (r) => r.locationMismatchFlag)) {
      cols.push({
        title: "Loc Mismatch",
        dataIndex: "locationMismatchFlag",
        key: "locationMismatchFlag",
        width: 120,
        render: (v) => (v ? <Tag color="orange">YES</Tag> : <Tag>NO</Tag>),
      });
    }

    if (columnHasData(visits, (r) => r.images?.length)) {
      cols.push({
        title: "Images",
        dataIndex: "images",
        key: "images",
        width: 90,
        render: (imgs) => imgs?.length || 0,
      });
    }

    cols.push({
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right",
      render: (_, r) => (
        <Button
          size="small"
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => openVisit(r)}
        >
          View
        </Button>
      ),
    });

    return cols;
    // eslint-disable-next-line
  }, [visits]);

  /* ============================================================
     RENDER
  ============================================================ */
  return (
    <div className="staff-dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
        <div>
          <h2>{staffName}</h2>
          <p>{staffUsername}</p>
          <p style={{ margin: 0, fontSize: 12, color: "#888" }}>
            {startDateStr === endDateStr
              ? moment(startDateStr).format("DD MMM YYYY")
              : `${moment(startDateStr).format("DD MMM YYYY")} — ${moment(
                  endDateStr
                ).format("DD MMM YYYY")}`}
          </p>
        </div>
      </div>

      {/* Filters + Export */}
      <Card className="filter-card">
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Space wrap>
              <CalendarOutlined />
              <RangePicker
                value={dateRange}
                allowClear={false}
                format="DD-MM-YYYY"
                onChange={handleDateRangeChange}
                disabledDate={(current) =>
                  current && current > moment().endOf("day")
                }
                presets={[
                  { label: "Today", value: [moment(), moment()] },
                  {
                    label: "Yesterday",
                    value: [
                      moment().subtract(1, "days"),
                      moment().subtract(1, "days"),
                    ],
                  },
                  {
                    label: "Last 7 Days",
                    value: [moment().subtract(6, "days"), moment()],
                  },
                  {
                    label: "Last 30 Days",
                    value: [moment().subtract(29, "days"), moment()],
                  },
                ]}
              />
              <Button
                type="primary"
                onClick={() => setDateRange([moment(), moment()])}
              >
                Today
              </Button>
              <Button
                onClick={() =>
                  setDateRange([
                    moment().subtract(1, "days"),
                    moment().subtract(1, "days"),
                  ])
                }
              >
                Yesterday
              </Button>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleExportExcel}
              className="export-btn"
            >
              Export to Excel
            </Button>
          </Col>
        </Row>
      </Card>

      {/* KPI Cards */}
      {summary && (
        <Row gutter={16} className="summary-row">
          <Col xs={24} md={12} lg={6}>
            <Card className="gradient-card visits-card">
              <Statistic
                title="Total Visits"
                value={summary.totalVisits || 0}
                prefix={<UserOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} lg={6}>
            <Card className="gradient-card amount-card">
              <Statistic
                title="Total Amount"
                value={summary.totalCashAmount || 0}
                prefix={<DollarOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} lg={6}>
            <Card className="gradient-card collection-card">
              <Statistic
                title="Collections"
                value={summary.cashCollection || 0}
                prefix={<WalletOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} md={12} lg={6}>
            <Card className="gradient-card drop-card">
              <Statistic
                title="Cash Drop"
                value={summary.cashDrop || 0}
                prefix={<BankOutlined />}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Distance alert */}
      {stats.farCount > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={`${stats.farCount} visit(s) marked more than 500m away from registered shop location`}
        />
      )}

      {/* Tabs */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "timeline",
              label: (
                <span>
                  <UnorderedListOutlined /> Timeline
                </span>
              ),
              children: renderTimeline(),
            },
            {
              key: "table",
              label: (
                <span>
                  <TableOutlined /> Table ({visits.length})
                </span>
              ),
              children: renderTable(),
            },
          ]}
        />
      </Card>

      {/* Modal */}
      <Modal
        open={modalVisible}
        width={1100}
        footer={null}
        onCancel={() => setModalVisible(false)}
        title={
          <Space>
            <EyeOutlined />
            Visit Details — {selectedVisit && getDisplayName(selectedVisit)}
          </Space>
        }
      >
        {selectedVisit && renderModalContent()}
      </Modal>
    </div>
  );

  /* ============================================================
     TIMELINE VIEW
  ============================================================ */
  function renderTimeline() {
    if (loading) {
      return (
        <div className="center-loader">
          <Spin size="large" />
        </div>
      );
    }
    if (visits.length === 0) {
      return <Empty description="No Visits Found" />;
    }

    return (
      <Row gutter={20}>
        <Col xs={24} lg={16}>
          <div className="journey-container">
            {visits.map((visit, index) => {
              const gap = getGap(index);
              return (
                <div key={visit._id}>
                  {index > 0 && (
                    <>
                      {gap != null && (
                        <div className="travel-gap">
                          <CarOutlined />
                          <span>{gap} mins travel gap</span>
                        </div>
                      )}
                      {visits[index - 1]?.distanceToNext && (
                        <div className="distance-info">
                          <SwapOutlined />
                          <span>
                            Distance:{" "}
                            {visits[index - 1].distanceToNext.distanceText} ·
                            Est. travel:{" "}
                            {visits[index - 1].distanceToNext.duration}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  <div className="journey-step">
                    <div className="timeline-marker">
                      <div
                        className="timeline-dot"
                        style={{ background: getVisitColor(visit.visitType) }}
                      />
                      {index !== visits.length - 1 && (
                        <div className="timeline-line" />
                      )}
                    </div>

                    <Card
                      hoverable
                      className="visit-card"
                      style={{
                        borderLeft: `5px solid ${getVisitColor(
                          visit.visitType
                        )}`,
                      }}
                      onClick={() => openVisit(visit)}
                    >
                      <Row justify="space-between" align="middle">
                        <Col flex="auto">
                          <h3 style={{ margin: 0 }}>
                            {getDisplayName(visit)}
                            <EyeOutlined
                              style={{ marginLeft: 8, color: "#1677ff" }}
                            />
                          </h3>
                          <Space wrap style={{ marginTop: 6 }}>
                            <Tag
                              color={getVisitColor(visit.visitType)}
                              icon={getVisitIcon(visit.visitType)}
                            >
                              {getVisitLabel(visit.visitType)}
                            </Tag>
                            {visit.status && visit.status !== "completed" && (
                              <Tag color="orange">{visit.status}</Tag>
                            )}
                            {visit.duplicateNewVisit && (
                              <Tag
                                className="duplicate-tag"
                                icon={<WarningOutlined />}
                              >
                                DUPLICATE MOBILE
                              </Tag>
                            )}
                            {visit.locationMismatchFlag && (
                              <Tag
                                className="warning-tag"
                                icon={<WarningOutlined />}
                              >
                                LOCATION MISMATCH
                              </Tag>
                            )}
                          </Space>
                        </Col>
                        <Col>
                          <div style={{ textAlign: "right" }}>
                            <div>
                              <strong style={{ fontSize: 15 }}>
                                {moment(visit.visitDate).format("hh:mm A")}
                              </strong>
                            </div>
                            <div style={{ fontSize: 11, color: "#888" }}>
                              {moment(visit.visitDate).format("DD MMM YYYY")}
                            </div>
                            {visit.latitude && visit.longitude && (
                              <Button
                                type="link"
                                size="small"
                                icon={<GlobalOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openMaps(visit.latitude, visit.longitude);
                                }}
                                style={{
                                  color: "#C9262C",
                                  padding: 0,
                                  marginTop: 2,
                                }}
                              >
                                Visit Map
                              </Button>
                            )}
                          </div>
                        </Col>
                      </Row>

                      {/* ===== INFO ROW ===== */}
                      {visit.visitType === "sr_telecom" ? (
                        // ===== SR TELECOM INFO =====
                        <div
                          className="shop-info-row"
                          style={{ marginTop: 10 }}
                        >
                          {visit.outletName && (
                            <span>
                              <ShopOutlined />{" "}
                              <strong>{visit.outletName}</strong>
                            </span>
                          )}
                          {visit.ownerName && (
                            <span>
                              <UserOutlined /> {visit.ownerName}
                            </span>
                          )}
                          {visit.mobile && (
                            <span>
                              <PhoneOutlined />{" "}
                              <strong>Agent: {visit.mobile}</strong>
                            </span>
                          )}
                          {visit.village && (
                            <span>
                              <HomeOutlined /> {visit.village}
                            </span>
                          )}
                        </div>
                      ) : (
                        (visit.shopName ||
                          visit.ownerName ||
                          visit.mobile ||
                          visit.shopType) && (
                          <div
                            className="shop-info-row"
                            style={{ marginTop: 10 }}
                          >
                            {visit.shopName && (
                              <span>
                                <ShopOutlined />{" "}
                                <strong>{visit.shopName}</strong>
                              </span>
                            )}
                            {visit.ownerName && (
                              <span>
                                <UserOutlined /> {visit.ownerName}
                              </span>
                            )}
                            {visit.mobile && (
                              <span>
                                <PhoneOutlined /> {visit.mobile}
                              </span>
                            )}
                            {visit.shopType && (
                              <span className="shop-type-tag">
                                {visit.shopType}
                              </span>
                            )}
                          </div>
                        )
                      )}

                      {/* ===== SR TELECOM DATES ===== */}
                      {visit.visitType === "sr_telecom" &&
                        (visit.topupDate || visit.paymentDate) && (
                          <div
                            className="shop-info-row"
                            style={{ marginTop: 8 }}
                          >
                            {visit.topupDate && (
                              <span>
                                <ClockCircleOutlined /> Topup:{" "}
                                <strong>
                                  {moment(visit.topupDate).format(
                                    "DD-MM-YYYY"
                                  )}
                                </strong>
                              </span>
                            )}
                            {visit.paymentDate && (
                              <span>
                                <DollarOutlined /> Payment:{" "}
                                <strong>
                                  {moment(visit.paymentDate).format(
                                    "DD-MM-YYYY"
                                  )}
                                </strong>
                              </span>
                            )}
                          </div>
                        )}

                      {/* ===== ADDRESS (skip for SR Telecom) ===== */}
                      {visit.visitType !== "sr_telecom" &&
                        (visit.shopAddress ||
                          visit.shopCity ||
                          visit.shopState ||
                          visit.shopPostcode) && (
                          <div
                            className="shop-info-row"
                            style={{ marginTop: 4 }}
                          >
                            <span>
                              <HomeOutlined />{" "}
                              {[
                                visit.shopAddress,
                                visit.shopCity,
                                visit.shopState,
                                visit.shopPostcode,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </div>
                        )}

                      {/* ===== SHOP LOCATION (skip for SR Telecom) ===== */}
                      {visit.visitType !== "sr_telecom" &&
                        visit.shopLatitude != null &&
                        visit.shopLongitude != null && (
                          <div
                            className="shop-info-row"
                            style={{ marginTop: 4 }}
                          >
                            <span>
                              <EnvironmentOutlined /> Shop:{" "}
                              {visit.shopLatitude}, {visit.shopLongitude}
                            </span>
                            <Button
                              type="link"
                              size="small"
                              icon={<GlobalOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                openMaps(
                                  visit.shopLatitude,
                                  visit.shopLongitude
                                );
                              }}
                              style={{ color: "#722ed1", padding: 0 }}
                            >
                              Shop Map
                            </Button>
                          </div>
                        )}

                      {/* ===== DISTANCE TO SHOP ===== */}
                      {visit.distanceToShop && (
                        <div
                          className={`distance-to-shop ${
                            visit.distanceToShop.isFar ? "far" : "near"
                          }`}
                        >
                          <AimOutlined /> {visit.distanceToShop.distanceText}{" "}
                          from registered shop
                        </div>
                      )}

                      {/* ===== BALANCES ===== */}
                      {(hasBalance(visit.walletBalance) ||
                        hasBalance(visit.khataBalance) ||
                        hasBalance(visit.externalBalance)) && (
                        <div className="balance-chips">
                          <BalanceChip
                            label={LABEL_WALLET}
                            value={visit.walletBalance}
                            icon={<WalletOutlined />}
                          />
                          <BalanceChip
                            label={LABEL_SRPAY}
                            value={-visit.khataBalance}
                            icon={<CreditCardOutlined />}
                          />
                          <BalanceChip
                            label={LABEL_SRKHATA}
                            value={visit.externalBalance}
                            icon={<GlobalOutlined />}
                          />
                        </div>
                      )}

                      {visit.visitTitle && (
                        <div
                          style={{
                            marginTop: 8,
                            color: "#722ed1",
                            fontWeight: 500,
                          }}
                        >
                          <FileTextOutlined /> {visit.visitTitle}
                        </div>
                      )}

                      {visit.cmsName && (
                        <div style={{ marginTop: 6, color: "#444" }}>
                          <BankOutlined /> <strong>CMS:</strong>{" "}
                          {visit.cmsName}
                        </div>
                      )}

                      {visit.amount > 0 && (
                        <div className="visit-amount">
                          ₹{Number(visit.amount).toLocaleString()}
                        </div>
                      )}

                      {visit.remark && (
                        <p className="remark-text">{visit.remark}</p>
                      )}

                      {visit.images?.length > 0 && (
                        <div className="thumb-row">
                          <PictureOutlined /> {visit.images.length} Images
                        </div>
                      )}
                    </Card>
                  </div>
                </div>
              );
            })}
          </div>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Journey Stats">
            <Divider />

            <div className="stats-box">
              <div>
                <ClockCircleOutlined /> <span>First Visit</span>
              </div>
              <strong>
                {stats.first
                  ? moment(stats.first.visitDate).format("hh:mm A")
                  : "-"}
              </strong>
            </div>

            <div className="stats-box">
              <div>
                <ClockCircleOutlined /> <span>Last Visit</span>
              </div>
              <strong>
                {stats.last
                  ? moment(stats.last.visitDate).format("hh:mm A")
                  : "-"}
              </strong>
            </div>

            <div className="stats-box">
              <span>Active Hours</span>
              <strong>{stats.activeHours || "-"}</strong>
            </div>

            <div className="stats-box">
              <span>Avg Time per Visit</span>
              <strong>{stats.avgVisit || "-"}</strong>
            </div>

            <div className="stats-box">
              <span>Total Visits</span>
              <strong>{stats.totalVisits ?? 0}</strong>
            </div>

            <Divider />

            <div className="stats-box">
              <div>
                <CarOutlined /> <span>Total Travel Distance</span>
              </div>
              <strong>{fmtDistance(stats.totalDistance)}</strong>
            </div>

            <div className="stats-box">
              <div>
                <ClockCircleOutlined /> <span>Total Travel Time</span>
              </div>
              <strong>
                {stats.totalDuration > 0
                  ? `${stats.totalDuration} mins`
                  : "0 mins"}
              </strong>
            </div>

            <div className="stats-box" style={{ borderBottom: "none" }}>
              <div>
                <AimOutlined /> <span>Avg Distance from Shop</span>
              </div>
              <strong>{fmtDistance(stats.avgShopDistance)}</strong>
            </div>
          </Card>
        </Col>
      </Row>
    );
  }

  /* ============================================================
     TABLE VIEW
  ============================================================ */
  function renderTable() {
    if (loading) {
      return (
        <div className="center-loader">
          <Spin size="large" />
        </div>
      );
    }
    if (!visits.length) {
      return <Empty description="No Visits Found" />;
    }

    return (
      <Table
        rowKey={(r) => r._id}
        dataSource={visits}
        columns={tableColumns}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: "max-content" }}
        size="small"
        bordered
        rowClassName={(r) =>
          r.duplicateNewVisit || r.locationMismatchFlag ? "row-flagged" : ""
        }
      />
    );
  }

  /* ============================================================
     MODAL CONTENT
  ============================================================ */
  function renderModalContent() {
    const v = selectedVisit;

    return (
      <>
        <Row gutter={20}>
          <Col span={14}>
            <Descriptions
              column={1}
              bordered
              size="small"
              labelStyle={{ width: 220, fontWeight: 600 }}
            >
              <Descriptions.Item label="Visit Type">
                <Tag
                  color={getVisitColor(v.visitType)}
                  icon={getVisitIcon(v.visitType)}
                >
                  {getVisitLabel(v.visitType)}
                </Tag>
                {v.duplicateNewVisit && (
                  <Tag className="duplicate-tag" style={{ marginLeft: 8 }}>
                    DUPLICATE MOBILE
                  </Tag>
                )}
                {v.locationMismatchFlag && (
                  <Tag className="warning-tag" style={{ marginLeft: 8 }}>
                    LOCATION MISMATCH
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Date & Time">
                {moment(v.visitDate).format("DD-MM-YYYY hh:mm A")}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={v.status === "completed" ? "green" : "orange"}>
                  {showVal(v.status)}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Shop Name">
                <ShopOutlined /> {showVal(v.shopName)}
              </Descriptions.Item>
              <Descriptions.Item label="Owner Name">
                <UserOutlined /> {showVal(v.ownerName)}
              </Descriptions.Item>
              <Descriptions.Item
                label={v.visitType === "sr_telecom" ? "Agent Mobile" : "Mobile"}
              >
                <PhoneOutlined /> {showVal(v.mobile)}
              </Descriptions.Item>
              <Descriptions.Item label="Shop Type">
                <AppstoreOutlined /> {showVal(v.shopType)}
              </Descriptions.Item>

              <Descriptions.Item label="Outlet Name">
                <IdcardOutlined /> {showVal(v.outletName)}
              </Descriptions.Item>

              {/* ===== SR Telecom fields ===== */}
              {v.topupDate ? (
                <Descriptions.Item label="Topup Date">
                  <ClockCircleOutlined />{" "}
                  {moment(v.topupDate).format("DD-MM-YYYY")}
                </Descriptions.Item>
              ) : null}
              {v.paymentDate ? (
                <Descriptions.Item label="Payment Date">
                  <DollarOutlined />{" "}
                  {moment(v.paymentDate).format("DD-MM-YYYY")}
                </Descriptions.Item>
              ) : null}
              {v.village ? (
                <Descriptions.Item label="Village">
                  <HomeOutlined /> {showVal(v.village)}
                </Descriptions.Item>
              ) : null}

              <Descriptions.Item label="Visit Title">
                <FileTextOutlined /> {showVal(v.visitTitle)}
              </Descriptions.Item>
              <Descriptions.Item label="CMS Name">
                <BankOutlined /> {showVal(v.cmsName)}
              </Descriptions.Item>
              <Descriptions.Item label="Amount">
                {v.amount != null && v.amount !== "" ? (
                  <span style={{ color: "#c9262c", fontWeight: 700 }}>
                    ₹{Number(v.amount).toLocaleString()}
                  </span>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Remark">
                {showVal(v.remark)}
              </Descriptions.Item>

              {hasBalance(v.walletBalance) && (
                <Descriptions.Item label={LABEL_WALLET}>
                  <span
                    style={{
                      color:
                        Number(v.walletBalance) < 0 ? "#cf1322" : "#389e0d",
                      fontWeight: 600,
                    }}
                  >
                    {fmtMoney(v.walletBalance)}
                  </span>
                </Descriptions.Item>
              )}

              {hasBalance(v.khataBalance) && (
                <Descriptions.Item label={LABEL_SRPAY}>
                  <span
                    style={{
                      color:
                        Number(v.khataBalance) > 0 ? "#cf1322" : "#389e0d",
                      fontWeight: 600,
                    }}
                  >
                    {fmtMoney(-v.khataBalance)}
                  </span>
                </Descriptions.Item>
              )}

              {hasBalance(v.externalBalance) && (
                <Descriptions.Item label={LABEL_SRKHATA}>
                  <span
                    style={{
                      color:
                        Number(v.externalBalance) < 0
                          ? "#cf1322"
                          : "#389e0d",
                      fontWeight: 600,
                    }}
                  >
                    {fmtMoney(v.externalBalance)}
                  </span>
                </Descriptions.Item>
              )}

              <Descriptions.Item label="Duplicate New Visit">
                {v.duplicateNewVisit ? (
                  <Tag color="red">YES</Tag>
                ) : (
                  <Tag>NO</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Location Mismatch">
                {v.locationMismatchFlag ? (
                  <Tag color="orange">YES</Tag>
                ) : (
                  <Tag>NO</Tag>
                )}
              </Descriptions.Item>

              <Descriptions.Item label="Distance to Shop">
                {v.distanceToShop ? v.distanceToShop.distanceText : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Distance to Next">
                {v.distanceToNext ? v.distanceToNext.distanceText : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Travel Time to Next">
                {v.distanceToNext ? v.distanceToNext.duration : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Images Count">
                {v.images?.length || 0}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Locations</Divider>

            <Card
              size="small"
              className="location-card visit-location"
              title={
                <Space>
                  <EnvironmentOutlined /> Marked Visit Location
                </Space>
              }
            >
              <p>
                <strong>Lat:</strong> {showVal(v.latitude)}{" "}
                <strong>Long:</strong> {showVal(v.longitude)}
              </p>
              {buildMapsUrl(v.latitude, v.longitude) && (
                <>
                  <a
                    href={buildMapsUrl(v.latitude, v.longitude)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: "break-all", fontSize: 12 }}
                  >
                    {buildMapsUrl(v.latitude, v.longitude)}
                  </a>
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="primary"
                      icon={<CompassOutlined />}
                      onClick={() => openMaps(v.latitude, v.longitude)}
                    >
                      Navigate to Visit
                    </Button>
                  </div>
                </>
              )}
            </Card>

            <Card
              size="small"
              className="location-card shop-location"
              title={
                <Space>
                  <HomeOutlined /> Registered Shop Location
                </Space>
              }
            >
              <p>
                <strong>Lat:</strong> {showVal(v.shopLatitude)}{" "}
                <strong>Long:</strong> {showVal(v.shopLongitude)}
              </p>
              <p>
                <strong>Address:</strong> {showVal(v.shopAddress)}
              </p>
              <p>
                <strong>City:</strong> {showVal(v.shopCity)}{" "}
                <strong>State:</strong> {showVal(v.shopState)}{" "}
                <strong>PIN:</strong> {showVal(v.shopPostcode)}
              </p>
              {buildMapsUrl(v.shopLatitude, v.shopLongitude) && (
                <>
                  <a
                    href={buildMapsUrl(v.shopLatitude, v.shopLongitude)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ wordBreak: "break-all", fontSize: 12 }}
                  >
                    {buildMapsUrl(v.shopLatitude, v.shopLongitude)}
                  </a>
                  <div style={{ marginTop: 8 }}>
                    <Button
                      icon={<CompassOutlined />}
                      onClick={() =>
                        openMaps(v.shopLatitude, v.shopLongitude)
                      }
                    >
                      Navigate to Shop
                    </Button>
                  </div>
                </>
              )}
            </Card>

            {v.distanceToShop && (
              <Alert
                className="distance-alert"
                type={v.distanceToShop.isFar ? "warning" : "success"}
                showIcon
                icon={<AimOutlined />}
                message={`Distance between Visit and Registered Shop: ${v.distanceToShop.distanceText}`}
                description={
                  v.distanceToShop.isFar
                    ? "Visit was marked more than 500m away from the registered shop location."
                    : "Visit was marked close to the registered shop location."
                }
              />
            )}
          </Col>

          <Col span={10}>
            {v.latitude && v.longitude && (
              <div className="map-container">
                <iframe
                  title="visit-location"
                  width="100%"
                  height="260"
                  frameBorder="0"
                  src={`https://maps.google.com/maps?q=${v.latitude},${v.longitude}&z=15&output=embed`}
                />
                <div className="map-overlay">
                  <Button
                    type="primary"
                    icon={<GlobalOutlined />}
                    onClick={() => openMaps(v.latitude, v.longitude)}
                  >
                    Open Visit in Maps
                  </Button>
                </div>
              </div>
            )}
            {v.shopLatitude && v.shopLongitude && (
              <div className="map-container">
                <iframe
                  title="shop-location"
                  width="100%"
                  height="260"
                  frameBorder="0"
                  src={`https://maps.google.com/maps?q=${v.shopLatitude},${v.shopLongitude}&z=15&output=embed`}
                />
                <div className="map-overlay">
                  <Button
                    type="primary"
                    icon={<GlobalOutlined />}
                    onClick={() => openMaps(v.shopLatitude, v.shopLongitude)}
                  >
                    Open Shop in Maps
                  </Button>
                </div>
              </div>
            )}
          </Col>
        </Row>

        <Divider />

        {v.images?.length > 0 && (
          <Image.PreviewGroup>
            <Space wrap>
              {v.images.map((img, i) => (
                <Image
                  key={i}
                  width={120}
                  height={120}
                  src={img.url}
                  style={{ objectFit: "cover", borderRadius: 8 }}
                />
              ))}
            </Space>
          </Image.PreviewGroup>
        )}
      </>
    );
  }
}

export default StaffVisitDetails;